import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bell, BellOff, Clock, Trash2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('cardioai_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DAYS = [
  { id: 0, name: 'Domingo' },
  { id: 1, name: 'Segunda' },
  { id: 2, name: 'Terça' },
  { id: 3, name: 'Quarta' },
  { id: 4, name: 'Quinta' },
  { id: 5, name: 'Sexta' },
  { id: 6, name: 'Sábado' }
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i.toString().padStart(2, '0'),
  label: `${i.toString().padStart(2, '0')}:00`
}));

const NotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const [reminders, setReminders] = useState([]);
  const [newReminder, setNewReminder] = useState({ day: '1', hour: '08' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Check notification permission
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    
    // Load reminders from localStorage
    const savedReminders = localStorage.getItem('workout_reminders');
    if (savedReminders) {
      setReminders(JSON.parse(savedReminders));
    }
    
    setLoading(false);
  }, []);

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('O teu navegador não suporta notificações');
      return;
    }

    const result = await Notification.requestPermission();
    setPermission(result);
    
    if (result === 'granted') {
      toast.success('Notificações ativadas!');
      // Test notification
      new Notification('Process Body AI', {
        body: 'Notificações ativadas com sucesso! 💪',
        icon: '/logo192.png'
      });
    } else {
      toast.error('Permissão negada para notificações');
    }
  };

  const addReminder = () => {
    const newR = {
      id: Date.now(),
      day: parseInt(newReminder.day),
      hour: parseInt(newReminder.hour),
      enabled: true
    };
    
    const updated = [...reminders, newR];
    setReminders(updated);
    localStorage.setItem('workout_reminders', JSON.stringify(updated));
    toast.success('Lembrete adicionado!');
  };

  const toggleReminder = (id) => {
    const updated = reminders.map(r => 
      r.id === id ? { ...r, enabled: !r.enabled } : r
    );
    setReminders(updated);
    localStorage.setItem('workout_reminders', JSON.stringify(updated));
  };

  const deleteReminder = (id) => {
    const updated = reminders.filter(r => r.id !== id);
    setReminders(updated);
    localStorage.setItem('workout_reminders', JSON.stringify(updated));
    toast.success('Lembrete removido');
  };

  // Check and send notifications
  useEffect(() => {
    if (permission !== 'granted') return;

    const checkReminders = () => {
      const now = new Date();
      const currentDay = now.getDay();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      reminders.forEach(reminder => {
        if (reminder.enabled && 
            reminder.day === currentDay && 
            reminder.hour === currentHour && 
            currentMinute === 0) {
          new Notification('Process Body AI - Hora de Treinar! 💪', {
            body: 'Não te esqueças do teu treino hoje!',
            icon: '/logo192.png',
            tag: `reminder-${reminder.id}`
          });
        }
      });
    };

    // Check every minute
    const interval = setInterval(checkReminders, 60000);
    return () => clearInterval(interval);
  }, [reminders, permission]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="notifications-page">
        <div className="mb-8">
          <h1 
            className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-2"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            <span className="text-[#32CD32]">Lembretes</span> de Treino
          </h1>
          <p className="text-gray-400">
            Configura lembretes para nunca perderes um treino
          </p>
        </div>

        {/* Permission Card */}
        <Card className="bg-[#121212] border-white/10 mb-6">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                {permission === 'granted' ? (
                  <Bell className="w-6 h-6 text-[#32CD32]" />
                ) : (
                  <BellOff className="w-6 h-6 text-gray-500" />
                )}
                <div>
                  <CardTitle className="text-lg">Notificações</CardTitle>
                  <CardDescription>
                    {permission === 'granted' 
                      ? 'Notificações ativadas' 
                      : permission === 'denied'
                        ? 'Notificações bloqueadas no navegador'
                        : 'Ativa as notificações para receber lembretes'}
                  </CardDescription>
                </div>
              </div>
              {permission !== 'granted' && permission !== 'denied' && (
                <Button 
                  className="btn-primary"
                  onClick={requestPermission}
                >
                  Ativar
                </Button>
              )}
              {permission === 'denied' && (
                <p className="text-xs text-red-400 sm:max-w-[200px]">
                  Ativa as notificações nas definições do navegador
                </p>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Add Reminder */}
        {permission === 'granted' && (
          <Card className="bg-[#121212] border-white/10 mb-6">
            <CardHeader>
              <CardTitle 
                className="text-lg uppercase tracking-tight"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Adicionar Lembrete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2 flex-1 min-w-[140px]">
                  <Label className="text-gray-300">Dia da Semana</Label>
                  <Select 
                    value={newReminder.day} 
                    onValueChange={(v) => setNewReminder({ ...newReminder, day: v })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/20">
                      {DAYS.map(day => (
                        <SelectItem key={day.id} value={day.id.toString()}>
                          {day.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex-1 min-w-[100px]">
                  <Label className="text-gray-300">Hora</Label>
                  <Select 
                    value={newReminder.hour} 
                    onValueChange={(v) => setNewReminder({ ...newReminder, hour: v })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/20 max-h-[200px]">
                      {HOURS.map(hour => (
                        <SelectItem key={hour.value} value={hour.value}>
                          {hour.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  className="btn-primary"
                  onClick={addReminder}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reminders List */}
        {permission === 'granted' && (
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle 
                className="text-lg uppercase tracking-tight"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Os Teus Lembretes
              </CardTitle>
              <CardDescription>
                {reminders.length === 0 
                  ? 'Ainda não tens lembretes configurados' 
                  : `${reminders.filter(r => r.enabled).length} lembretes ativos`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reminders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Adiciona o teu primeiro lembrete</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reminders.map(reminder => (
                    <div 
                      key={reminder.id}
                      className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                        reminder.enabled 
                          ? 'bg-[#32CD32]/5 border-[#32CD32]/30' 
                          : 'bg-black/30 border-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          reminder.enabled ? 'bg-[#32CD32]/20' : 'bg-gray-800'
                        }`}>
                          <Bell className={`w-5 h-5 ${reminder.enabled ? 'text-[#32CD32]' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <p className="font-medium">
                            {DAYS.find(d => d.id === reminder.day)?.name}
                          </p>
                          <p className="text-sm text-gray-400">
                            às {reminder.hour.toString().padStart(2, '0')}:00
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={reminder.enabled}
                          onCheckedChange={() => toggleReminder(reminder.id)}
                          className="data-[state=checked]:bg-[#32CD32]"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteReminder(reminder.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
